import React,
{
  useRef,
  useState,
  useEffect,
  useCallback,
  CSSProperties,
} from "react";
import { theme } from "@/styles/theme";

type ScrollAreaProps = {
  children: React.ReactNode;
  style?: CSSProperties;
  contentStyle?: CSSProperties;
};

type ScrollMetrics = {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
};

export function ScrollArea({ children, style, contentStyle }: ScrollAreaProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [metrics, setMetrics] = useState<ScrollMetrics>({
    scrollTop: 0,
    scrollHeight: 0,
    clientHeight: 0,
  });

  const [thumbDragging, setThumbDragging] = useState(false);
  const dragState = useRef({
    startY: 0,
    startScrollTop: 0,
  });

  const [thumbHovered, setThumbHovered] = useState(false);
  const [thumbPressed, setThumbPressed] = useState(false);

  const updateMetrics = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    setMetrics({
      scrollTop: el.scrollTop,
      scrollHeight: el.scrollHeight,
      clientHeight: el.clientHeight,
    });
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    updateMetrics();

    el.addEventListener("scroll", updateMetrics);
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateMetrics);
      resizeObserver.observe(el);
    }

    return () => {
      el.removeEventListener("scroll", updateMetrics);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [updateMetrics]);

  // Only allow scrolling when there is real overflow
  const canScroll = metrics.scrollHeight > metrics.clientHeight + 1;

  // Track spans the whole height of the area (with tiny insets)
  const trackInsetTop = 2;
  const trackInsetBottom = 2;
  const trackHeight = Math.max(
    0,
    metrics.clientHeight - trackInsetTop - trackInsetBottom
  );

  let thumbHeight = 0;
  let thumbTop = 0;

  if (trackHeight > 0 && metrics.scrollHeight > 0 && canScroll) {
    const ratio = metrics.clientHeight / metrics.scrollHeight;
    const minThumb = 20;
    thumbHeight = Math.max(minThumb, trackHeight * ratio);

    const maxThumbTravel = trackHeight - thumbHeight;
    const maxScroll =
      metrics.scrollHeight - metrics.clientHeight > 0
        ? metrics.scrollHeight - metrics.clientHeight
        : 1;

    const scrollRatio = metrics.scrollTop / maxScroll;
    thumbTop = trackInsetTop + maxThumbTravel * scrollRatio;
  }

  const handleThumbMouseMove = (e: MouseEvent) => {
    if (!thumbDragging) return;
    const el = viewportRef.current;
    if (!el) return;

    const deltaY = e.clientY - dragState.current.startY;
    if (trackHeight <= 0 || thumbHeight <= 0) return;

    const maxThumbTravel = trackHeight - thumbHeight;
    const maxScroll =
      metrics.scrollHeight - metrics.clientHeight > 0
        ? metrics.scrollHeight - metrics.clientHeight
        : 1;

    const scrollPerPixel = maxScroll / maxThumbTravel;
    const newScrollTop =
      dragState.current.startScrollTop + deltaY * scrollPerPixel;

    el.scrollTop = Math.max(0, Math.min(maxScroll, newScrollTop));
  };

  const handleThumbMouseUp = () => {
    setThumbDragging(false);
    setThumbPressed(false);
    window.removeEventListener("mousemove", handleThumbMouseMove);
    window.removeEventListener("mouseup", handleThumbMouseUp);
  };

  const handleThumbMouseDown = (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (!canScroll) return;

    e.preventDefault();
    e.stopPropagation();
    const el = viewportRef.current;
    if (!el) return;

    setThumbDragging(true);
    setThumbPressed(true);

    dragState.current.startY = e.clientY;
    dragState.current.startScrollTop = el.scrollTop;

    window.addEventListener("mousemove", handleThumbMouseMove);
    window.addEventListener("mouseup", handleThumbMouseUp);
  };

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleThumbMouseMove);
      window.removeEventListener("mouseup", handleThumbMouseUp);
    };
  }, []);

  // Win95 thumb skin (no buttons)
  const baseThumbStyle: CSSProperties = {
    position: "absolute",
    left: 0,
    width: 16,
    background: "#000000",
    boxShadow:
      "-1px -1px 0 0 #000 inset, 1px 1px 0 0 #EBEBEB inset, -2px -2px 0 0 #818181 inset",
    border: "none",
    padding: 0,
    cursor: canScroll ? "grab" : "default",
  };

  const hoverThumbStyle: CSSProperties = {
    background: "#363636",
  };

  const pressedThumbStyle: CSSProperties = {
    background: "#191919",
    boxShadow:
      "-1px -1px 0 0 #000 inset, 1px 1px 0 0 #7BFF8E inset, -2px -2px 0 0 #1C982E inset",
    cursor: canScroll ? "grabbing" : "default",
  };

  const disabledThumbStyle: CSSProperties = {
    opacity: 0.35,
    cursor: "default",
  };

  const thumbStyle: CSSProperties = (() => {
    let s: CSSProperties = { ...baseThumbStyle };
    if (!canScroll) {
      s = { ...s, ...disabledThumbStyle };
      return s;
    }
    if (thumbPressed) s = { ...s, ...pressedThumbStyle };
    else if (thumbHovered) s = { ...s, ...hoverThumbStyle };
    return s;
  })();

  const showCustomThumb =
    canScroll && trackHeight > 0 && thumbHeight > 0;

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        width: "100%",
        height: "100%",
        ...style,
      }}
    >
      {/* Scrollable content (native scroll hidden visually by global CSS) */}
      <div
        ref={viewportRef}
        className="lf-scroll-viewport"
        style={{
          position: "relative",
          flex: 1,
          height: "100%",
          overflowY: canScroll ? "auto" : "hidden",
          overflowX: "hidden",
          paddingRight: canScroll ? 16 : 0, // reserve space for custom bar
          boxSizing: "border-box",
        }}
      >
        <div style={contentStyle}>{children}</div>
      </div>

      {/* Custom Win95 scrollbar track – inside the outer container */}
      {canScroll && (
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 16,
            height: "100%",
            background: "#000000",
            boxShadow:
              "-1px -1px 0 0 #000 inset, 1px 1px 0 0 #EBEBEB inset, -2px -2px 0 0 #818181 inset",
          }}
        >
          {showCustomThumb && (
            <button
              type="button"
              onMouseDown={handleThumbMouseDown}
              onMouseEnter={() => setThumbHovered(true)}
              onMouseLeave={() => {
                if (!thumbDragging) setThumbHovered(false);
              }}
              style={{
                ...thumbStyle,
                height: thumbHeight,
                top: thumbTop,
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
